/**
 * @ngdoc    directive
 * @name     csm.ui.validators.directive:urlValidator
 * @restrict A
 * @param    {string=} urlValidator     Error popup title.
 * @param    {string=} csmRequired      "false"=value is not required.
 * @param    {string=} disableValidator "true"=skip validation.
 *
 * @description
 * Validator to verify that an input value is a valid HTTP or HTTPS URL.
 * We work to ensure that any URL entered with unsafe extension is blocked to prevent
 * phishing or other malicious activity. This is a defense-in-depth measure in addition
 * to validating the URL on the server..
 **/
/* @ngInject */
export function urlValidator(CSMValidatorService) {
  /**
   * Why each file types are blocked:
   *
   * Executables and scripts (.exe, .msi, .bat, .cmd, .ps1, .sh, .dll, .vbs, '.scr', '.pif', '.jar'):
   * - Executable files are among the easiest to weaponize. They can install hidden malware or initiate
   *   ransomware as soon as they’re run.
   *
   * Compressed archives (.zip, .rar, .7z, .tar, .gz):
   * - Archives can bundle multiple infected files and hide them behind passwords or encryption.
   *   Once extracted, the hidden malware deploys immediately. Attackers also layer archives inside
   *   other archives to evade scanning tools.
   *
   * Documents (.pdf, .doc, .docx, .xls, .xlsx, .ppt, .pptx):
   * - PDF File -> are the second most common file type used to deliver malware. Because they’re trusted
   *   for invoices, policies, and contracts, employees rarely think twice before opening one. But PDFs
   *   often contain embedded JavaScript, macros, or hidden URLs that execute the moment the file loads.
   * - Word Documents -> Word documents remain one of a phisher’s favorite weapons. Embedded macros can
   *   silently install malware or create a backdoor into your network. Newer AI-powered tools can even
   *   rewrite macro code to avoid signature-based detection, such as antivirus software.
   * - Excel Spreadsheets -> Attackers love spreadsheets because they’re full of formulas, links, and
   *   embedded data that look normal but can be twisted into malware triggers.
   * - PowerPoints -> Malicious PowerPoint files hide harmful scripts behind images and animations. All
   *   it takes is one click during a live presentation to unleash the attack.
   *
   * Images (.jpg, .jpeg, .png, .gif, .bmp, .svg, .webp, .ico, .tiff, .tif):
   * - Image files can be weaponized with steganography, a technique that hides malicious code within
   *   the pixels of an image. When the image is viewed or downloaded, the hidden code can execute
   *   and compromise the user’s system. Attackers often use this method to bypass traditional
   */
  const BLOCKED_EXTENSIONS = [
    // Executables and scripts
    '.exe',
    '.msi',
    '.bat',
    '.cmd',
    '.ps1',
    '.sh',
    '.dll',
    '.vbs',
    '.scr',
    '.pif',
    '.jar',
    // Compressed archives
    '.zip',
    '.rar',
    '.7z',
    '.tar',
    '.gz',
    // Documents
    '.pdf',
    '.doc',
    '.docx',
    '.xls',
    '.xlsx',
    '.ppt',
    '.pptx',
    // Images
    '.jpg',
    '.jpeg',
    '.png',
    '.gif',
    '.bmp',
    '.svg',
    '.webp',
    '.ico',
    '.tiff',
    '.tif'
  ];

  /* Returns the file extension of a string (e.g. "report.zip" -> ".zip"). */
  const getExtension = (str) => {
    const dot = str.lastIndexOf('.');
    return dot !== -1 ? str.slice(dot) : '';
  };

  return CSMValidatorService.makeDirective({
    doValidate: function (value) {
      const error = CSMValidatorService.BaseValidator.prototype.doValidate.call(
        this,
        value
      );
      if (error) return error;
      if (!value) return null;

      /**
       * Use the browser's native URL parser to validate structure.
       * Prepend https:// if no scheme is present, mirroring the
       * server-side normalization in UrlValidation.cs, so that bare
       * domains like "example.com" are accepted.
       */

      // Reject any URL that already carries a non-http(s) scheme.
      if (value.includes('://') && !/^https?:\/\//i.test(value))
        return 'URL must begin with http:// or https://';

      const normalized = /^https?:\/\//i.test(value)
        ? value
        : 'https://' + value;
      let parsed;
      try {
        parsed = new URL(normalized);
      } catch (e) {
        return 'The entered URL is not valid';
      }

      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:')
        return 'URL must begin with http:// or https://';

      /**
       * Why parsed hostname and pathname instead of regex on the raw string:
       * A regex operating on the full URL string cannot reliably isolate where
       * the hostname ends and the path begins, and query strings or fragments
       * after the path mean endsWith() on the full URL is easily bypassed.
       * new URL() gives us clean, already-parsed properties so each part can
       * be checked in isolation without any string-slicing guesswork.
       *
       * Why hostname and pathname are checked separately:
       * - hostname is the domain (e.g. www.example.com). Some blocked extensions
       *   are also real TLDs (.zip, .sh, .gz), so a URL whose hostname ends with
       *   one of them (e.g. www.example.zip) would have a clean path of "/" and
       *   would pass a path-only check entirely.
       * - pathname is the path after the domain (e.g. /files/report.zip). We
       *   split it into segments rather than calling endsWith() on the whole path
       *   because a trailing segment after the extension (e.g. /report.zip/check)
       *   would cause endsWith() to match "/check" and miss the blocked extension.
       */
      const blockedSet = new Set(BLOCKED_EXTENSIONS);

      const hostname = parsed.hostname.toLowerCase();
      if (blockedSet.has(getExtension(hostname)))
        return 'URL points to a restricted file type';

      /**
       * decodeURIComponent before splitting so that percent-encoded characters
       * are resolved first — a dot encoded as %2E would otherwise bypass the
       * extension check. This mirrors Uri.UnescapeDataString() on the server.
       * Malformed percent-encoding can throw, so treat decode failures as a
       * restricted URL instead of letting validation throw.
       */
      let decodedPathname;
      try {
        decodedPathname = decodeURIComponent(parsed.pathname);
      } catch (e) {
        return 'URL points to a restricted file type';
      }

      const segments = decodedPathname.toLowerCase().split('/');
      for (const segment of segments)
        if (blockedSet.has(getExtension(segment)))
          return 'URL points to a restricted file type';

      return null;
    }
  });
}
